import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { SseService } from 'src/app/services/SseService';
import { TemaService } from 'src/app/services/tema.services';
import { MetaApiService, PeriodoHistoricoFila, PeriodoHistoricoTope, UnidadHistorico } from 'src/app/services/meta-api.service';
import { ConfirmDialogComponent } from '../../common/confirm-dialog/confirm-dialog.component';

/** Clave de la fuente en la fila (`fmp` / `mt`) y su código en el backend. */
export type FuenteHistorico = 'fmp' | 'mt';

export interface PeriodoHistoricoRow {
  periodo: number;
  timeframe: string;
  /** Null cuando el tope no aplica a la fuente en ese periodo. */
  fmp: PeriodoHistoricoTope | null;
  mt: PeriodoHistoricoTope;
  /** Valores cargados del backend, para saber si la fila tiene cambios sin guardar. */
  original: Record<FuenteHistorico, PeriodoHistoricoTope | null>;
}

const ETIQUETAS: Record<string, string> = {
  '1m': '1 Minuto', '5m': '5 Minutos', '15m': '15 Minutos', '30m': '30 Minutos',
  '1h': '1 Hora', '4h': '4 Horas', '1d': 'Diario', '1w': 'Semanal',
};

/** Mismos topes que valida el backend (PeriodoHistoricoDaoImpl.maximo): 100 años en cada unidad. */
export const MAX_POR_UNIDAD: Record<UnidadHistorico, number> = { M: 1200, A: 100 };

@Component({
  selector: 'app-admin-periodos-historico',
  templateUrl: './admin-periodos-historico.component.html',
  styleUrls: ['./admin-periodos-historico.component.scss'],
})
export class AdminPeriodosHistoricoComponent implements OnInit, OnDestroy {
  readonly unidades: { valor: UnidadHistorico; etiqueta: string }[] = [
    { valor: 'M', etiqueta: 'Meses' },
    { valor: 'A', etiqueta: 'Años' },
  ];
  readonly fuentes: { clave: FuenteHistorico; codigo: 'FMP' | 'MT'; etiqueta: string }[] = [
    { clave: 'fmp', codigo: 'FMP', etiqueta: 'FMP' },
    { clave: 'mt', codigo: 'MT', etiqueta: 'MetaTrader (MetaAPI)' },
  ];
  readonly displayedColumns = ['periodo', ...this.fuentes.flatMap(f => [f.clave + 'Cantidad', f.clave + 'Unidad', f.clave + 'Desde'])];
  readonly columnasGrupo = ['grupoPeriodo', ...this.fuentes.map(f => 'grupo-' + f.clave)];
  filas: PeriodoHistoricoRow[] = [];
  isLoading = false;
  isBorrando = false;

  private theme = 'light-theme';
  private temaSub?: Subscription;

  constructor(
    private sseService: SseService,
    private cookieService: CookieService,
    private toastr: ToastrService,
    private metaApiService: MetaApiService,
    private dialog: MatDialog,
    private temaService: TemaService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.temaSub = this.temaService.valDarkTheme.subscribe(dark => {
      this.theme = dark ? 'dark-theme' : 'light-theme';
    });
    this.cargar();
  }

  ngOnDestroy(): void {
    this.temaSub?.unsubscribe();
  }

  etiqueta(timeframe: string): string {
    return ETIQUETAS[timeframe] ?? timeframe;
  }

  maximo(tope: PeriodoHistoricoTope): number {
    return MAX_POR_UNIDAD[tope.unidad] ?? MAX_POR_UNIDAD.M;
  }

  esValido(tope: PeriodoHistoricoTope | null): boolean {
    if (!tope) return true;
    return Number.isInteger(tope.cantidad) && tope.cantidad >= 1 && tope.cantidad <= this.maximo(tope);
  }

  tieneCambiosFuente(fila: PeriodoHistoricoRow, fuente: FuenteHistorico): boolean {
    const actual = fila[fuente];
    const original = fila.original[fuente];
    if (!actual || !original) return false;
    return actual.cantidad !== original.cantidad || actual.unidad !== original.unidad;
  }

  tieneCambios(fila: PeriodoHistoricoRow): boolean {
    return this.fuentes.some(f => this.tieneCambiosFuente(fila, f.clave));
  }

  /** Fecha desde la que se descargaría hoy con el tope dado (mismo cálculo que historicalFromDate). */
  desde(tope: PeriodoHistoricoTope | null): Date | null {
    if (!tope || !this.esValido(tope)) return null;
    const d = new Date();
    if (tope.unidad === 'A') d.setFullYear(d.getFullYear() - tope.cantidad);
    else d.setMonth(d.getMonth() - tope.cantidad);
    return d;
  }

  get hayCambios(): boolean {
    return this.filas.some(f => this.tieneCambios(f));
  }

  get todoValido(): boolean {
    return this.filas.every(f => this.esValido(f.fmp) && this.esValido(f.mt));
  }

  cargar(): void {
    this.isLoading = true;
    this.sseService.apiGetNew('fmp/periodosHistorico').subscribe({
      next: (data: PeriodoHistoricoFila[]) => {
        this.filas = (Array.isArray(data) ? data : [])
          .filter(f => !!f?.mt)
          .map(f => ({
            periodo: f.periodo,
            timeframe: f.timeframe,
            fmp: f.fmp ? { ...f.fmp } : null,
            mt: { ...f.mt },
            original: { fmp: f.fmp ? { ...f.fmp } : null, mt: { ...f.mt } },
          }));
        this.isLoading = false;
        this.cdRef.markForCheck();
      },
      error: (error: any) => {
        console.error('Error al cargar el tope de histórico:', error);
        this.toastr.error('No fue posible cargar el tope de histórico', 'Error', { timeOut: 5000 });
        this.isLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  guardar(): void {
    if (this.isLoading || !this.hayCambios || !this.todoValido) return;

    const payload = this.filas.flatMap(fila => this.fuentes
      .filter(f => this.tieneCambiosFuente(fila, f.clave))
      .map(f => ({ periodo: fila.periodo, fuente: f.codigo, cantidad: fila[f.clave]!.cantidad, unidad: fila[f.clave]!.unidad })));

    this.isLoading = true;
    this.sseService.apiPostNew('admin/periodosHistorico', payload, this.cookieService.get('auth_token')).subscribe({
      next: () => {
        this.filas.forEach(fila => {
          fila.original = { fmp: fila.fmp ? { ...fila.fmp } : null, mt: { ...fila.mt } };
        });
        // Que las descargas de esta misma pestaña usen ya los valores nuevos.
        this.metaApiService.cargarPeriodosHistorico();
        this.toastr.success('Tope de histórico actualizado', 'Éxito', { timeOut: 5000 });
        this.isLoading = false;
        this.cdRef.markForCheck();
      },
      error: (error: any) => {
        console.error('Error al guardar el tope de histórico:', error);
        this.toastr.error(error?.error?.message || 'No fue posible guardar el tope de histórico', 'Error', { timeOut: 5000 });
        this.isLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  /** Borra todas las velas de MetaAPI (todos los activos, periodos y cuentas) para redescargarlas desde cero. */
  borrarDatosMetaApi(): void {
    if (this.isBorrando) return;
    this.dialog.open(ConfirmDialogComponent, {
      width: '420px',
      panelClass: this.theme,
      data: {
        titulo: 'Borrar datos de MetaAPI',
        icon: 'delete_forever',
        msj: 'Se borran todas las velas descargadas de MetaTrader (MetaAPI) de todos los activos, periodos y cuentas, ' +
             'junto con su estado de sincronización. La próxima descarga las volverá a traer desde cero con el tope vigente.\n\n' +
             'Los datos de FMP no se tocan. Esta acción es irreversible.',
        botonAceptar: 'Sí, borrar',
        botonCancelar: 'Cancelar',
        destructivo: true,
      }
    }).afterClosed().subscribe(ok => {
      if (ok) this._borrarDatosMetaApiConfirmado();
    });
  }

  private _borrarDatosMetaApiConfirmado(): void {
    this.isBorrando = true;
    this.cdRef.markForCheck();
    this.sseService.apiPostNew('admin/metaapi/borrarDatos', {}, this.cookieService.get('auth_token')).subscribe({
      next: (res: any) => {
        const velas = Number(res?.velas ?? 0).toLocaleString();
        this.toastr.success(`Se borraron ${velas} velas de MetaAPI`, 'Éxito', { timeOut: 5000 });
        this.isBorrando = false;
        this.cdRef.markForCheck();
      },
      error: (error: any) => {
        console.error('Error al borrar los datos de MetaAPI:', error);
        this.toastr.error(error?.error?.message || 'No fue posible borrar los datos de MetaAPI', 'Error', { timeOut: 5000 });
        this.isBorrando = false;
        this.cdRef.markForCheck();
      }
    });
  }
}
